import React, { useState, useEffect, useCallback, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import DashboardLayout from "../../../components/layout/DashboardLayout";
import Button from "../../../components/button/Button";
import Input from "../../../components/input/Input";
import Select from "react-select";
import ResourceList from "../../../components/resourceList/ResourceList";
import Checkbox from "../../../components/checkbox/Checkbox";
import styles from "./BulkCreateBillingInvoicesScreen.module.scss";
import moment from "moment";
import _ from "lodash";
import {
  selectInvoiceSchema,
  selectAllInvoiceSchemas,
  fetchInvoiceSchemaIds,
  bulkCreateBillingInvoices,
  bulkCreateBillingInvoicesResetState,
} from "../../../store/actions/admin/billingInvoices";
import billingInvoicesService from "../../../apis/services/admin/billingInvoicesService";

const BILLING_PERIOD_OPTIONS = [
  { value: "monthly", label: "Mesečno" },
  { value: "quarterly", label: "Kvartalno" },
  { value: "semi-annual", label: "Polugodišnje" },
  { value: "annual", label: "Godišnje" },
];

const HEADER_VALUES = [
  { label: "", key: "checkbox", sortable: false },
  { label: "NAZIV", key: "name", sortable: true },
  { label: "KOMPANIJA", key: "companyName", sortable: false },
  { label: "FAKTURISKA KOMPANIJA", key: "billingCompanyName", sortable: false },
  { label: "TIP PERIODA", key: "defaultBillingPeriodType", sortable: false },
  { label: "AKTIVNA", key: "isActive", sortable: false },
];

const BulkCreateBillingInvoicesScreen = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const [schemas, setSchemas] = useState({
    page: 1,
    perPage: 100,
    total: 0,
    data: [],
    sortBy: "id",
    order: "DESC",
    attempt: false,
  });
  const [loading, setLoading] = useState(false);
  const sortRef = useRef({ sortBy: "id", order: "DESC" });
  const searchRef = useRef("");
  const selectedSchemas = useSelector((state) => state.billingInvoices.selectedSchemas.data);
  const selectAll = useSelector((state) => state.billingInvoices.selectedSchemas.selectAll);
  const allSchemaIds = useSelector((state) => state.billingInvoices.selectedSchemas.allSchemaIds);
  const schemaIdsData = useSelector((state) => state.billingInvoices.schemaIds);
  const bulkCreate = useSelector((state) => state.billingInvoices.bulkCreate);

  const [billingPeriodStart, setBillingPeriodStart] = useState("");
  const [billingPeriodEnd, setBillingPeriodEnd] = useState("");
  const [billingPeriodType, setBillingPeriodType] = useState({ value: "monthly", label: "Mesečno" });
  const [searchTerm, setSearchTerm] = useState("");
  const [filterActive, setFilterActive] = useState(true);

  const loadSchemas = useCallback(async (page = 1, sortBy = "id", order = "DESC", search = "") => {
    sortRef.current = { sortBy, order };
    searchRef.current = search;
    setSchemas((prev) => ({ ...prev, attempt: true }));
    setLoading(true);
    try {
      const response = await billingInvoicesService.getInvoiceSchemas(page, 100, sortBy, order, search);
      const schemasData = response.data?.data?.data || response.data?.data || response.data || [];
      const transformedData = Array.isArray(schemasData) ? schemasData : [];
      const responsePage = response.data?.data?.page || response.data?.page || page;
      const responsePerPage = response.data?.data?.perPage || response.data?.perPage || 100;
      const responseTotal = response.data?.data?.total || response.data?.total || transformedData.length;
      
      setSchemas({
        page: responsePage,
        perPage: responsePerPage,
        total: responseTotal,
        data: transformedData,
        sortBy,
        order,
        attempt: false,
      });
    } catch (error) {
      console.error("Error loading invoice schemas:", error);
      toast.error("Greška pri učitavanju šema faktura");
      setSchemas((prev) => ({
        ...prev,
        data: [],
        attempt: false,
      }));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSchemas(1, "id", "DESC", "");
    
    // Don't fetch schema IDs on initial load if we have preselected schemas
    const preselectedSchemas = location.state?.preselectedSchemas;
    if (preselectedSchemas && Array.isArray(preselectedSchemas) && preselectedSchemas.length > 0) {
      return;
    }
  }, [dispatch, loadSchemas]);

  // Fetch schema IDs only when filter changes (not on initial load)
  useEffect(() => {
    const preselectedSchemas = location.state?.preselectedSchemas;
    if (preselectedSchemas && Array.isArray(preselectedSchemas) && preselectedSchemas.length > 0) {
      return; // Skip if we have preselected schemas
    }
    
    // Only fetch when filterActive changes, not on initial mount
    if (filterActive !== undefined) {
      dispatch(fetchInvoiceSchemaIds(filterActive ? true : null));
    }
  }, [filterActive, dispatch]);

  // Handle preselected schemas separately to avoid dependency issues
  useEffect(() => {
    const preselectedSchemas = location.state?.preselectedSchemas;
    if (preselectedSchemas && Array.isArray(preselectedSchemas) && preselectedSchemas.length > 0) {
      // Clear any existing selections first
      dispatch(selectAllInvoiceSchemas(false, []));
      // Small delay to ensure state is cleared
      setTimeout(() => {
        // Select each schema
        preselectedSchemas.forEach(schema => {
          dispatch(selectInvoiceSchema(schema));
        });
      }, 100);
    }
  }, [location.state, dispatch]);

  useEffect(() => {
    if (billingPeriodType && billingPeriodStart) {
      const startDate = moment(billingPeriodStart);
      let endDate;
      const periodType = typeof billingPeriodType === "object" ? billingPeriodType.value : billingPeriodType;
      switch (periodType) {
        case "monthly":
          endDate = startDate.clone().add(1, "month").subtract(1, "day").endOf("day");
          break;
        case "quarterly":
          endDate = startDate.clone().add(3, "months").subtract(1, "day").endOf("day");
          break;
        case "semi-annual":
          endDate = startDate.clone().add(6, "months").subtract(1, "day").endOf("day");
          break;
        case "annual":
          endDate = startDate.clone().add(1, "year").subtract(1, "day").endOf("day");
          break;
        default:
          return;
      }
      setBillingPeriodEnd(endDate.format("YYYY-MM-DD"));
    }
  }, [billingPeriodType, billingPeriodStart]);

  useEffect(() => {
    if (bulkCreate.success) {
      const data = bulkCreate.data;
      const summary = data?.summary || {};
      toast.success(
        `Uspešno kreirano: ${summary.generated || 0}, Preskočeno: ${summary.skipped || 0}, Greške: ${summary.errors || 0}`
      );
      dispatch(bulkCreateBillingInvoicesResetState());
      // Clear selections
      dispatch(selectAllInvoiceSchemas(false, []));
      setBillingPeriodStart("");
      setBillingPeriodEnd("");
    }
  }, [bulkCreate.success, dispatch]);

  useEffect(() => {
    if (bulkCreate.error) {
      toast.error("Greška pri masovnom kreiranju faktura");
      dispatch(bulkCreateBillingInvoicesResetState());
    }
  }, [bulkCreate.error, dispatch]);

  const handleSelectAll = () => {
    if (selectAll) {
      dispatch(selectAllInvoiceSchemas(false, []));
    } else {
      // Try multiple possible response structures
      const ids = schemaIdsData.data?.data?.ids || 
                  schemaIdsData.data?.ids || 
                  (schemaIdsData.data?.data?.schemas?.map(s => s.id) || []) ||
                  (schemaIdsData.data?.schemas?.map(s => s.id) || []);
      
      if (ids.length === 0) {
        // Fallback to current page schemas
        const currentPageIds = schemas.data.map(s => s.id);
        if (currentPageIds.length === 0) {
          toast.warning("Nema dostupnih šema za izbor");
          return;
        }
        dispatch(selectAllInvoiceSchemas(true, currentPageIds));
      } else {
        dispatch(selectAllInvoiceSchemas(true, ids));
      }
    }
  };

  const handleSelectSchema = (schema) => {
    dispatch(selectInvoiceSchema(schema));
  };

  const isSchemaSelected = (schema) => {
    if (selectAll) {
      // If select all is active, check if schema is in allSchemaIds
      return allSchemaIds.includes(schema.id);
    }
    return selectedSchemas.some((s) => s.id === schema.id);
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Double check - prevent any form submission
    if (e && e.target && e.target.form) {
      e.target.form.onsubmit = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        return false;
      };
    }

    console.log("handleSubmit - State check:", {
      selectedSchemas,
      selectAll,
      allSchemaIds,
      selectedSchemasLength: selectedSchemas.length,
      allSchemaIdsLength: allSchemaIds.length
    });

    if (selectedSchemas.length === 0 && !selectAll) {
      toast.error("Molimo izaberite barem jednu šemu fakture");
      return;
    }

    if (!billingPeriodStart) {
      toast.error("Molimo unesite datum početka perioda");
      return;
    }

    if (!billingPeriodEnd) {
      toast.error("Molimo unesite datum završetka perioda");
      return;
    }

    // Get schema IDs - use allSchemaIds if selectAll is true, otherwise use selected schemas
    // Ensure all IDs are integers
    let schemaIds = [];
    
    if (selectAll) {
      console.log("Using selectAll - allSchemaIds:", allSchemaIds);
      schemaIds = allSchemaIds.map(id => {
        const parsed = parseInt(id, 10);
        return !isNaN(parsed) && parsed > 0 ? parsed : null;
      }).filter(id => id !== null);
    } else {
      console.log("Using selectedSchemas:", selectedSchemas);
      schemaIds = selectedSchemas.map((s) => {
        if (!s || !s.id) return null;
        const parsed = parseInt(s.id, 10);
        return !isNaN(parsed) && parsed > 0 ? parsed : null;
      }).filter(id => id !== null);
    }

    console.log("Final schemaIds array:", schemaIds);

    if (schemaIds.length === 0) {
      toast.error("Molimo izaberite barem jednu šemu fakture");
      return;
    }

    const bulkData = {
      schemaIds,
      billingPeriodStart: moment(billingPeriodStart).startOf("day").toISOString(),
      billingPeriodEnd: moment(billingPeriodEnd).endOf("day").toISOString(),
      billingPeriodType: typeof billingPeriodType === "object" ? billingPeriodType.value : billingPeriodType,
    };

    console.log("Submitting bulk create with data:", JSON.stringify(bulkData, null, 2));
    console.log("schemaIds type:", typeof schemaIds, "isArray:", Array.isArray(schemaIds), "length:", schemaIds.length);
    
    try {
      await dispatch(bulkCreateBillingInvoices(bulkData));
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      toast.error("Greška pri kreiranju faktura");
    }
  };

  const handleLoadPage = useCallback((page) => {
    loadSchemas(page, sortRef.current.sortBy, sortRef.current.order, searchRef.current);
  }, [loadSchemas]);

  const handleSort = useCallback((sortBy, order) => {
    loadSchemas(1, sortBy, order, searchRef.current);
  }, [loadSchemas]);

  const handleSearch = useCallback(
    _.debounce((searchValue) => {
      loadSchemas(1, sortRef.current.sortBy, sortRef.current.order, searchValue);
    }, 500),
    [loadSchemas]
  );

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    handleSearch(value);
  };

  const filteredSchemas = schemas.data || [];
  const displaySchemas = filterActive 
    ? filteredSchemas.filter((s) => s.isActive !== false)
    : filteredSchemas;

  const rowRender = (schema) => {
    return (
      <div key={schema.id} className={styles.tableRow}>
        <div className={styles.tableCell}>
          <Checkbox
            onCheck={() => handleSelectSchema(schema)}
            checked={isSchemaSelected(schema)}
          />
        </div>
        <div className={styles.tableCell}>
          <strong>{schema.name || "-"}</strong>
        </div>
        <div className={styles.tableCell}>
          {schema.company?.name || schema.companyId || "-"}
        </div>
        <div className={styles.tableCell}>
          {schema.billingCompany?.name || schema.billingCompanyId || "-"}
        </div>
        <div className={styles.tableCell}>
          {schema.defaultBillingPeriodType === "monthly" && "Mesečno"}
          {schema.defaultBillingPeriodType === "quarterly" && "Kvartalno"}
          {schema.defaultBillingPeriodType === "semi-annual" && "Polugodišnje"}
          {schema.defaultBillingPeriodType === "annual" && "Godišnje"}
          {!schema.defaultBillingPeriodType && "-"}
        </div>
        <div className={styles.tableCell}>
          <span className={schema.isActive ? styles.activeBadge : styles.inactiveBadge}>
            {schema.isActive ? "Da" : "Ne"}
          </span>
        </div>
      </div>
    );
  };

  const selectedCount = selectAll ? allSchemaIds.length : selectedSchemas.length;

  return (
    <DashboardLayout>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>Masovno kreiranje faktura</h1>
          <Button
            name="Nazad"
            onClick={() => history.push("/billing-invoices")}
          />
        </div>

        <div className={styles.form}>
          <div className={styles.formSection}>
            <h2 className={styles.sectionTitle}>Period fakturisanja</h2>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Tip perioda *</label>
                <Select
                  options={BILLING_PERIOD_OPTIONS}
                  value={billingPeriodType}
                  onChange={(selectedOption) => {
                    setBillingPeriodType(selectedOption);
                    if (billingPeriodStart) {
                      const startDate = moment(billingPeriodStart);
                      let endDate;
                      switch (selectedOption.value) {
                        case "monthly":
                          endDate = startDate.clone().add(1, "month").subtract(1, "day").endOf("day");
                          break;
                        case "quarterly":
                          endDate = startDate.clone().add(3, "months").subtract(1, "day").endOf("day");
                          break;
                        case "semi-annual":
                          endDate = startDate.clone().add(6, "months").subtract(1, "day").endOf("day");
                          break;
                        case "annual":
                          endDate = startDate.clone().add(1, "year").subtract(1, "day").endOf("day");
                          break;
                        default:
                          return;
                      }
                      setBillingPeriodEnd(endDate.format("YYYY-MM-DD"));
                    }
                  }}
                  placeholder="Izaberite tip perioda"
                  styles={{
                    container: (provided) => ({
                      ...provided,
                      width: "100%",
                    }),
                    control: (provided) => ({
                      ...provided,
                      minHeight: "50px",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      "&:hover": {
                        borderColor: "#007bff",
                      },
                    }),
                  }}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Datum početka *</label>
                <input
                  type="date"
                  className={styles.dateInput}
                  value={billingPeriodStart}
                  onChange={(e) => {
                    const value = e.target.value;
                    setBillingPeriodStart(value);
                    if (billingPeriodType && value) {
                      const startDate = moment(value);
                      let endDate;
                      const periodType = typeof billingPeriodType === "object" ? billingPeriodType.value : billingPeriodType;
                      switch (periodType) {
                        case "monthly":
                          endDate = startDate.clone().add(1, "month").subtract(1, "day").endOf("day");
                          break;
                        case "quarterly":
                          endDate = startDate.clone().add(3, "months").subtract(1, "day").endOf("day");
                          break;
                        case "semi-annual":
                          endDate = startDate.clone().add(6, "months").subtract(1, "day").endOf("day");
                          break;
                        case "annual":
                          endDate = startDate.clone().add(1, "year").subtract(1, "day").endOf("day");
                          break;
                        default:
                          return;
                      }
                      setBillingPeriodEnd(endDate.format("YYYY-MM-DD"));
                    }
                  }}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Datum završetka *</label>
                <input
                  type="date"
                  className={styles.dateInput}
                  value={billingPeriodEnd}
                  onChange={(e) => setBillingPeriodEnd(e.target.value)}
                  min={billingPeriodStart}
                />
                {billingPeriodStart && billingPeriodType && (
                  <p className={styles.helperText}>
                    Automatski izračunato na osnovu tipa perioda
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className={styles.formSection}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Izaberi šeme faktura</h2>
              <div className={styles.filterAndSearch}>
                <label className={styles.filterLabel}>
                  <input
                    type="checkbox"
                    checked={filterActive}
                    onChange={(e) => setFilterActive(e.target.checked)}
                    className={styles.filterCheckbox}
                  />
                  Samo aktivne
                </label>
                <div className={styles.searchWrapper}>
                  <Input
                    placeholder="Pretraži šeme..."
                    onChange={handleSearchChange}
                    value={searchTerm}
                  />
                </div>
              </div>
            </div>

            <div className={styles.selectedInfo}>
              <p>Izabrano: {selectedCount} šema</p>
            </div>

            <ResourceList
              resource={{
                page: schemas.page || 1,
                perPage: schemas.perPage || 100,
                total: displaySchemas.length,
                data: displaySchemas,
                sortBy: schemas.sortBy || "id",
                order: schemas.order || "DESC",
                attempt: loading,
              }}
              headerValues={HEADER_VALUES}
              selectable={true}
              onSelectAll={handleSelectAll}
              selectAll={selectAll}
              onLoadPage={handleLoadPage}
              onSort={handleSort}
              rowRender={rowRender}
              showLoader={true}
            />
          </div>

          <div className={styles.formActions}>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSubmit(e);
              }}
              disabled={bulkCreate.attempt || selectedCount === 0}
              className={styles.submitButton}
            >
              {bulkCreate.attempt ? "Kreiranje..." : `Kreiraj fakture (${selectedCount})`}
            </button>
            <Button
              name="Otkaži"
              onClick={() => history.push("/billing-invoices")}
              type="button"
            />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BulkCreateBillingInvoicesScreen;

